import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { SealCheck, FileArrowDown, WarningCircle } from "@phosphor-icons/react";

const fmtUK = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB"); };

export default function PendingApprovalsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null); // {mode:'reject'|'more_info', comp}
  const [notes, setNotes] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/training/pending").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const viewEvidence = async (c) => {
    const fid = c.certificate_url || (c.evidence && c.evidence[c.evidence.length - 1]?.file_id);
    if (!fid) { toast.info("No evidence attached"); return; }
    try { const r = await api.get(`/files/${fid}/url`); window.open(r.data.url, "_blank"); }
    catch (e) { toast.error("Could not open evidence"); }
  };
  const verify = async (c) => {
    try { await api.post(`/training/competencies/${c.id}/verify`); toast.success("Verified"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitAction = async () => {
    if (!notes.trim()) { toast.error("A reason is required"); return; }
    const url = action.mode === "reject" ? "reject" : "more-info";
    try { await api.post(`/training/competencies/${action.comp.id}/${url}`, { notes }); toast.success("Saved"); setAction(null); setNotes(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex flex-col gap-4 max-w-4xl" data-testid="pending-approvals-page">
      <div className="flex items-center gap-2.5">
        <SealCheck size={24} weight="bold" className="text-slate-800" />
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Pending Approvals</h1>
        <span className="ml-auto font-mono text-sm text-slate-400" data-testid="pending-count">{rows.length}</span>
      </div>
      {loading ? <p className="text-slate-400">Loading…</p> : rows.length === 0 ? (
        <p className="text-slate-400 text-sm" data-testid="pending-empty">Nothing awaiting verification.</p>
      ) : rows.map((c) => (
        <div key={c.id} data-testid={`pending-${c.id}`} className="bg-white border-2 border-slate-200 rounded-lg p-4 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-body font-semibold text-slate-900">{c.competency_name || "Competency"}</div>
              <div className="text-sm text-slate-600">{c.person_name}</div>
              <div className="font-mono text-xs text-slate-500 mt-1">
                {c.certificate_number ? `Ref ${c.certificate_number} · ` : ""}{c.no_expiry ? "No expiry" : (c.expiry_date ? `Expires ${fmtUK(c.expiry_date)}` : "")}
                {c.submitted_at ? ` · submitted ${fmtUK(c.submitted_at)}` : ""}{c.source === "self_service" ? " · self-submitted" : ""}
              </div>
              {c.verification_status === "more_info" && c.review_notes && (
                <div className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-1"><WarningCircle size={14} weight="bold" className="mt-0.5" />Returned: {c.review_notes}</div>
              )}
            </div>
            <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-bold uppercase bg-blue-100 text-blue-700">{c.verification_status === "more_info" ? "More info" : "Awaiting"}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid={`pending-evidence-${c.id}`} onClick={() => viewEvidence(c)} variant="outline" className="h-8 text-xs font-semibold"><FileArrowDown size={14} className="mr-1" />Evidence</Button>
            <Button data-testid={`pending-verify-${c.id}`} onClick={() => verify(c)} className="h-8 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700">Verify</Button>
            <Button data-testid={`pending-moreinfo-${c.id}`} onClick={() => { setAction({ mode: "more_info", comp: c }); setNotes(""); }} variant="outline" className="h-8 text-xs font-semibold text-amber-700 border-amber-300">Need more info</Button>
            <Button data-testid={`pending-reject-${c.id}`} onClick={() => { setAction({ mode: "reject", comp: c }); setNotes(""); }} variant="outline" className="h-8 text-xs font-semibold text-red-700 border-red-300">Reject</Button>
          </div>
        </div>
      ))}

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">{action?.mode === "reject" ? "Reject submission" : "Request more information"}</DialogTitle></DialogHeader>
          <Textarea data-testid="review-notes" placeholder="Reason / what's needed (recorded on the audit trail)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Button data-testid="review-submit" onClick={submitAction} className="bg-slate-900 hover:bg-slate-800 font-semibold">Save</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
